import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Inbox,
  Newspaper,
  LogOut,
  Menu,
  X,
  TreePine,
} from "lucide-react";
import api, { auth } from "../../lib/api";
import SEO from "../../components/SEO";
import { Toaster } from "../../components/ui/sonner";

const NAV = [
  { to: "/admin/forfragningar", label: "Förfrågningar", icon: Inbox },
  { to: "/admin/blogg", label: "Blogg", icon: Newspaper },
];

export default function AdminLayout() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    api
      .get("/auth/me")
      .then((r) => setMe(r.data))
      .catch(() => {
        localStorage.removeItem("tf_admin_token");
        navigate("/admin", { replace: true });
      });
  }, [navigate]);

  const logout = async () => {
    await auth.logout();
    navigate("/admin", { replace: true });
  };

  if (!me) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <SEO title="Admin" description="Adminläge" noindex path="/admin" />
        <p className="text-[#5A6258]">Laddar admin…</p>
      </div>
    );
  }

  return (
    <div data-testid="admin-layout" className="min-h-screen bg-[#F7F5F0]">
      <SEO
        title="Admin — Trädfällarna"
        description="Adminläge"
        path="/admin"
        noindex
      />
      {/* Top bar */}
      <header className="bg-[#1B2A1E] text-white sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/admin/forfragningar" className="flex items-center gap-3">
            <span className="inline-flex items-center justify-center w-9 h-9 rounded-md bg-white p-1">
              <img
                src="/images/logo.webp"
                alt="Trädfällarna"
                className="w-full h-full object-contain"
              />
            </span>
            <span className="font-heading font-extrabold text-base hidden sm:inline">
              Trädfällarna · Admin
            </span>
          </Link>
          <div className="flex items-center gap-3">
            <span
              data-testid="admin-current-email"
              className="hidden md:inline text-xs uppercase tracking-[0.14em] text-[#A8B0A4] font-semibold"
            >
              {me.email}
            </span>
            <button
              onClick={logout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-2 bg-white/10 hover:bg-[#D95D2E] text-white font-semibold px-3 py-2 rounded-md transition-colors text-sm"
            >
              <LogOut className="w-4 h-4" /> Logga ut
            </button>
            <button
              onClick={() => setOpen((v) => !v)}
              className="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-md hover:bg-white/10"
              aria-label="Öppna meny"
            >
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-10 grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10">
        {/* Sidebar */}
        <aside
          className={`lg:col-span-3 ${
            open ? "block" : "hidden"
          } lg:block`}
        >
          <nav
            data-testid="admin-nav"
            className="bg-white border border-[#E2E0D8] rounded-xl p-3"
          >
            {NAV.map((n) => {
              const Icon = n.icon;
              return (
                <NavLink
                  key={n.to}
                  to={n.to}
                  data-testid={`admin-nav-${n.to.split("/").pop()}`}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-4 py-3 rounded-lg font-semibold transition-colors ${
                      isActive
                        ? "bg-[#1B2A1E] text-white"
                        : "text-[#1B2A1E] hover:bg-[#EFEBDF]"
                    }`
                  }
                >
                  <Icon className="w-4 h-4" />
                  {n.label}
                </NavLink>
              );
            })}
          </nav>
          <Link
            to="/"
            className="mt-4 inline-flex items-center gap-2 text-sm text-[#5A6258] hover:text-[#D95D2E] font-semibold px-4"
          >
            <TreePine className="w-4 h-4" /> Tillbaka till sajten
          </Link>
        </aside>

        <main className="lg:col-span-9">
          <Outlet />
        </main>
      </div>
      <Toaster position="top-center" richColors closeButton />
    </div>
  );
}

export function AdminDashboardRedirect() {
  // Hit by /admin/dashboard or default catch
  return <LayoutDashboard className="hidden" />;
}
